// ! 从SortMenu修改而来，但是PC端的部分使用的是Popper而不是Menu

import Popup from '@/components/Popup';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { font } from '@/theme/zeelool.theme';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Button, MenuListProps, PopperProps } from '@mui/material';
import { CheckOutlined, CollapseArrowDownOutlined, WapCollapseDownFilled } from '@zeelool/icon';
import { useTranslations } from 'next-intl';
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import PopperMenu from './PopperMenu';

export interface SortOption {
  label: string;
  value: string | number;
}

interface SortMenuProps {
  options: SortOption[];
  value?: string | number;
  onChange: (value: string | number) => void;
  showValue?: boolean;
  notShowValueContent?: React.ReactNode;
  buttonStyle?: React.CSSProperties;
  popupTitle?: string;
  popupHeight?: string | number;
  showColon?: boolean; // 是否显示冒号
  onSortBtnClick?: () => void;
  // 新增：位置配置
  // ! anchorOrigin和 transformOrigin直接废除，这是 popover的 api，不适用于 popper
  // anchorOrigin?: {
  //   vertical: 'top' | 'center' | 'bottom';
  //   horizontal: 'left' | 'center' | 'right';
  // };
  // transformOrigin?: {
  //   vertical: 'top' | 'center' | 'bottom';
  //   horizontal: 'left' | 'center' | 'right';
  // };
  placement?: PopperProps['placement'];
  menuListProps?: MenuListProps;
}

export default function SortMenu({
  options,
  value,
  onChange,
  showValue = true,
  notShowValueContent,
  buttonStyle = {},
  popupTitle = '',
  popupHeight = 'auto',
  showColon = false,
  menuListProps = {},
  onSortBtnClick,
  placement = 'bottom-start',
}: SortMenuProps) {
  const t = useTranslations('sortMenu');
  const [popperOpen, setPopperOpen] = React.useState(false);
  const { deviceType } = useContext(ResponsiveContext);
  const [showPopup, setShowPopup] = React.useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleClick = useCallback(() => {
    if (deviceType === EPlatform.WEB) {
      setPopperOpen(true);
    } else {
      setShowPopup(true);
    }
    onSortBtnClick?.();
  }, [deviceType]);

  const handleClose = () => {
    setPopperOpen(false);
  };

  const handleSelect = useCallback(
    (_value: string | number) => {
      onChange(_value);
      handleClose();
      setShowPopup(false);
    },
    [onChange],
  );

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  // 当 popup 打开时，滚动到选中的选项
  useEffect(() => {
    if (showPopup && deviceType !== EPlatform.WEB && selectedOption) {
      // 使用 setTimeout 确保 DOM 已完全渲染
      setTimeout(() => {
        const selectedElement = document.getElementById(selectedOption.label);
        if (selectedElement) {
          selectedElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 100);
    }
  }, [showPopup, selectedOption, deviceType]);

  return (
    <div>
      {showValue && (
        <>
          {deviceType === EPlatform.WEB && (
            <span className="text-[14px]">{`${t('sortBy')}${showColon ? ':' : ''}`}&nbsp;</span>
          )}
        </>
      )}
      <Button
        ref={anchorRef}
        endIcon={
          deviceType === EPlatform.WEB ? (
            <CollapseArrowDownOutlined
              className={`!text-[16px] text-secondary-text transition-transform duration-300 ${popperOpen ? 'rotate-180' : ''}`}
            />
          ) : (
            <WapCollapseDownFilled
              className={`!text-[16px] text-secondary-text transition-transform duration-300 ${showPopup ? 'rotate-180' : ''}`}
            />
          )
        }
        onClick={handleClick}
        aria-expanded={popperOpen ? 'true' : undefined}
        aria-haspopup="true"
        sx={{
          color: 'text.primary',
          fontSize: '14px',
          '&:hover': {
            backgroundColor: 'transparent',
          },
          fontFamily: deviceType === EPlatform.WEB ? font.br : font.bb,
          '& .MuiButton-icon': {
            marginLeft: deviceType === EPlatform.WEB ? '8px' : '2px',
          },
          ...buttonStyle,
        }}
      >
        {showValue && <>{selectedOption?.label || ''}</>}
        {!showValue && notShowValueContent}
      </Button>
      <WebOnly>
        <PopperMenu
          anchorEl={anchorRef.current}
          open={popperOpen}
          onClose={handleClose}
          options={options}
          selectedValue={value}
          onSelect={handleSelect}
          menuListProps={menuListProps}
          placement={placement}
        />
      </WebOnly>
      <WapOnly>
        <Popup
          open={showPopup}
          onClose={() => setShowPopup(false)}
          position="bottom"
          title={popupTitle || t('title')}
          sx={{ '& .popup-content': { pt: 0, pb: 4 } }}
          height={popupHeight}
        >
          <div className="history-list">
            {options.map((option) => (
              <div
                key={option.value}
                className="flex items-center justify-between h-[45px] border-b border-[#eeeeee]"
                id={option.label}
                onClick={() => handleSelect(option.value)}
              >
                <div
                  className={`text-[14px] leading-[45px] ${option.value === value && 'text-primary-purple font-bb'}`}
                >
                  {option.label}
                </div>
                {option.value === value && (
                  <CheckOutlined className="text-26 text-primary-purple" />
                )}
              </div>
            ))}
          </div>
        </Popup>
      </WapOnly>
    </div>
  );
}
